import { defaultMaximumWordLimit, defaultMinimumWordLimit, trueMaximumWordLimit } from './constants'

// Based on word counter - https://gitlab.grammarly.io/growth/bloom/-/blob/master/packages/website-components/src/text-checker/components/word-counter/utils.ts
export interface TextAnalysis {
  words: number
  charactersWithSpaces: number
  charactersWithoutSpaces: number
  sentences: number
  paragraphs: number
  readingTime: string
  truncatedText?: string
}

export const analyzeTextAndMaybeTruncateToDesiredWordLimit = (
  text: string,
  desiredWordLimit?: number
): TextAnalysis => {
  let wordCount = 0
  const charCountWithSpaces = text.length
  let charCountWithoutSpaces = 0
  let sentenceCount = 0
  let paragraphCount = 0
  let truncatedWordCount = 0
  let truncatedText = ''

  const normalizedText = text.replace(/\r\n/g, '\n').trim()

  const paragraphs = normalizedText.split(/\n+/).filter((p) => p.length > 0)
  paragraphCount = paragraphs.length

  paragraphs.forEach((paragraph) => {
    const trimmedParagraph = paragraph.trim()

    if (trimmedParagraph.length > 0) {
      const wordsInParagraph = trimmedParagraph.split(/\s+/).filter((word) => word.length > 0)
      wordCount += wordsInParagraph.length

      const sentencesInParagraph = trimmedParagraph.split(/[.!?]+/).filter((s) => s.trim().length > 0)
      sentenceCount += sentencesInParagraph.length

      charCountWithoutSpaces += trimmedParagraph.replace(/[\s\n]+/g, '').length

      if (desiredWordLimit && truncatedWordCount < desiredWordLimit) {
        // Replace new lines and extra spaces with our own formatting. Potentially can find a better way to do this later.
        if (truncatedText !== '') {
          truncatedText += '\n\n'
        }
        wordsInParagraph.forEach((word) => {
          if (truncatedWordCount < desiredWordLimit) {
            truncatedText += word + ' '
            truncatedWordCount += 1
          }
        })
      }
    }
  })

  const readingTime = calculateReadingTime(wordCount)

  return {
    words: wordCount,
    charactersWithSpaces: charCountWithSpaces,
    charactersWithoutSpaces: charCountWithoutSpaces,
    sentences: sentenceCount,
    paragraphs: paragraphCount,
    readingTime,
    truncatedText,
  }
}

const calculateReadingTime = (wordCount: number): string => {
  const wordsPerMinute = 250
  const minutes = Math.floor(wordCount / wordsPerMinute)
  const seconds = Math.round((wordCount % wordsPerMinute) / (wordsPerMinute / 60))
  return `${minutes}m ${seconds}s`
}

export const getActualMaximumWordLimit = (maxWordLimit: number | undefined): number => {
  if (maxWordLimit === undefined) {
    return defaultMaximumWordLimit
  }

  if (maxWordLimit > trueMaximumWordLimit) {
    return trueMaximumWordLimit
  }

  return maxWordLimit
}

export const getActualMinimumWordLimit = (minWordLimit: number | undefined, maxWordLimit: number): number => {
  if (minWordLimit === undefined) {
    return defaultMinimumWordLimit
  }

  if (minWordLimit >= maxWordLimit) {
    if (defaultMinimumWordLimit >= maxWordLimit) {
      return 0
    }
    return defaultMinimumWordLimit
  }

  return minWordLimit
}

/**
 * Extracts and normalizes text from FormData field
 */
export const parseSingleFieldFormData = (toolInputName: string, formData: FormData): string => {
  const formValue = formData.get(toolInputName) || ''
  return typeof formValue === 'string' ? formValue.trim() : String(formValue).trim()
}

/**
 * Checks if the submitted text matches the sample text (trimmed comparison)
 * Used to determine if usage should be counted toward free-use limits
 */
export const isSampleTextSubmission = (submittedText: string, sampleText?: string): boolean => {
  if (!sampleText) {
    return false
  }
  return submittedText.trim() === sampleText.trim()
}
